// Der Tour-Streifen — die Abkürzung zurück in die laufende Tour.
//
// Marvins Anforderung: "Der Fahrer muss jederzeit, wenn er in die App geht, mit
// einem Klick in der Tour weitermachen können, wo er vorher stehen geblieben
// ist."
//
// Ein Knopf auf dem Startbildschirm hätte das nicht gelöst — er hätte erst
// zurück zum Start gemusst. Deshalb sitzt der Streifen direkt über der unteren
// Leiste und ist damit auf JEDER Seite da, so wie der Mini-Player einer
// Musik-App, der einen zurück ins laufende Lied bringt.
//
// Er zeigt sich NUR, wenn wirklich gearbeitet wird (siehe useLaufendeTour) —
// ein Hinweis, der immer leuchtet, wird nicht mehr gelesen.
//
// Wohin er führt, muss er nicht entscheiden: Die Tourseite steht von selbst in
// der Phase, in der der Fahrer aufgehört hat — Beladen, Fahren oder Rückladen.
// Der Streifen ist nur der kurze Weg dorthin.

import { Link, useLocation } from "react-router-dom";
import { useLaufendeTour } from "@/lib/tour";
import { streifenText } from "@/lib/laufende-tour";

export function TourStreifen() {
  const { pathname } = useLocation();
  const { data: tour } = useLaufendeTour();

  // Auf der Tourseite selbst wäre er nur eine zweite Tür in denselben Raum.
  if (!tour || pathname.startsWith("/crm/tour")) return null;

  const beladen = tour.phase === "beladen";
  const anteil = tour.gesamt ? (tour.fertig / tour.gesamt) * 100 : 0;
  const text = streifenText(tour.phase, tour.fertig, tour.gesamt);

  return (
    <Link
      to={`/crm/tour?datum=${tour.tour_date}`}
      aria-label={`${beladen ? "Beladen" : "Tour"} fortsetzen — ${text}`}
      className="relative shrink-0 border-t border-gruen-500/40 bg-gruen-500/10 transition-colors duration-fast active:bg-gruen-500/20"
    >
      {/* Der Fortschritt als dünner Faden am oberen Rand. Er sagt dasselbe wie
          die Zahl daneben, aber ohne Lesen — ein Blick genügt. */}
      <div className="absolute inset-x-0 top-0 h-0.5 bg-ink-700/60">
        <div
          className="h-full bg-gruen-500 transition-[width]"
          style={{ width: `${anteil}%` }}
        />
      </div>

      <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-2.5">
        {/* Der pulsende Punkt ist das eigentliche Signal: hier läuft etwas. */}
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gruen-500/70" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gruen-500" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold leading-tight text-ink-50">
            {beladen ? "Beladen läuft" : "Tour läuft"} · {tagKurz(tour.tour_date)}
          </span>
          <span className="block truncate text-xs leading-tight text-ink-400">{text}</span>
        </span>

        <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-gruen-400">
          weiter
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="m9 6 6 6-6 6" />
          </svg>
        </span>
      </div>
    </Link>
  );
}

/** "Do 23.07." — kurz genug, dass daneben noch der Fortschritt Platz hat. */
function tagKurz(iso: string) {
  const [j, m, t] = iso.split("-").map(Number);
  const d = new Date(j, m - 1, t);
  const TAGE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  return `${TAGE[d.getDay()]} ${String(t).padStart(2, "0")}.${String(m).padStart(2, "0")}.`;
}
